import React, { useEffect, useMemo, useState } from 'react';
import { Timer, Zap, Flag } from 'lucide-react';
import { easternDateTime, formatKickoff, kickoffPhase, splitDuration } from '../lib/kickoff';

const pad = (value) => String(value).padStart(2, '0');

export const KickoffCountdown = ({ id, date, time, compact = false }) => {
  const target = useMemo(() => easternDateTime(date, time), [date, time]);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!target) return undefined;
    const interval = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, [target]);

  if (!target) return null;
  const { phase, remaining } = kickoffPhase(target, now);
  const parts = splitDuration(remaining);
  const cells = [['Days', parts.days], ['Hrs', parts.hours], ['Min', parts.minutes], ['Sec', parts.seconds]].filter(([label]) => !(compact && label === 'Days' && parts.days === 0));

  if (phase !== 'upcoming') {
    const live = phase === 'live';
    return (
      <div data-testid={`kickoff-countdown-${id}`} data-phase={phase} className={`inline-flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] ${live ? 'bg-[#c8102e] text-white' : 'bg-[#0a1f44]/10 text-[#0a1f44]/70'}`}>
        {live ? <Zap className="w-3.5 h-3.5 animate-pulse" /> : <Flag className="w-3.5 h-3.5" />}
        {live ? `Game on · Kicked off ${formatKickoff(target)} ET` : 'Final · Thanks for cheering on the Patriots'}
      </div>
    );
  }

  return (
    <div data-testid={`kickoff-countdown-${id}`} data-phase="upcoming" className={`border-l-4 border-[#c8102e] bg-[#0a1f44] text-white ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}>
      <div className="flex items-center gap-1.5 font-heading uppercase tracking-[0.3em] text-[9px] text-white/60"><Timer className="w-3 h-3 text-[#f4b400]" /> Kickoff in · {formatKickoff(target)} ET</div>
      <div className="flex items-end gap-3 mt-1" aria-live="off">
        {cells.map(([label, value]) => (
          <div key={label} className="text-center min-w-[2.5rem]">
            <div className={`font-display tabular-nums leading-none ${compact ? 'text-xl' : 'text-2xl sm:text-3xl'}`} data-testid={`kickoff-countdown-${id}-${label.toLowerCase()}`}>{pad(value)}</div>
            <div className="font-heading uppercase tracking-widest text-[8px] text-white/55">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
